import { useEffect, useState } from "react";

import {
  getModelByPath,
  isReferencedNodeValue,
  isRelativeTimeDiffValue,
  ModelNotFoundException,
} from "@doitintl/cloudflow-commons";
import {
  BuiltInVariableType,
  type FloatApiServiceModelDescriptor,
  type IntegerApiServiceModelDescriptor,
  type ListApiServiceModelDescriptor,
  type MapApiServiceModelDescriptor,
  type Member,
  ModelType,
  type ReferencedNodeValue,
  type ReferencedTextAreaValue,
  type StringApiServiceModelDescriptor,
  type StructureApiServiceModelDescriptor,
  TimeFrequency,
  type TimestampApiServiceModelDescriptor,
  type UnwrappedApiServiceModelDescriptor,
} from "@doitintl/cmp-models";
import * as yup from "yup";

import {
  type NodeWitOutputModel,
  useReferencedFieldContext,
} from "./parameters/wrappers/ReferencedField/useReferencedFieldContext";
import { CUSTOM_TOKEN_PLACEHOLDER } from "./parameters/wrappers/ReferencedField/useReferencedFieldMenuItems";

type SchemaContext = {
  referenceableNodes: NodeWitOutputModel[];
  model: UnwrappedApiServiceModelDescriptor;
};

export type ReferencedNodeSchemaOptions = {
  requiredModelType?:
    | ModelType[]
    | ((
        referencedNodeModel: UnwrappedApiServiceModelDescriptor,
        context: yup.TestContext<yup.AnyObject>
      ) => boolean | yup.ValidationError);
  requiredModelTypeMessage?: string | ((label: string) => string);
  useParentErrorPath?: boolean;
};

const referencedNodeDefaultValue = { referencedField: [], referencedNodeId: "" };

export function createReferencedNodeSchema(
  referenceableNodes: NodeWitOutputModel[],
  { requiredModelType, requiredModelTypeMessage, useParentErrorPath }: ReferencedNodeSchemaOptions = {}
) {
  return yup
    .object({
      referencedNodeId: yup.string().defined().default(""),
      referencedField: yup.array(yup.string().required()).required().default([]),
    })
    .default(referencedNodeDefaultValue)
    .stripUnknown()
    .transform((value): ReferencedNodeValue => {
      if (!isReferencedNodeValue(value)) {
        return referencedNodeDefaultValue;
      }

      const referencedNodeModel = referenceableNodes.find(({ id }) => id === value.referencedNodeId)?.outputModel;
      if (!referencedNodeModel) {
        return referencedNodeDefaultValue;
      }

      try {
        getModelByPath(referencedNodeModel, value.referencedField);
      } catch (error) {
        if (error instanceof ModelNotFoundException) {
          return {
            referencedNodeId: value.referencedNodeId,
            referencedField: [],
          };
        }
        throw error;
      }

      return value;
    })
    .label("Referenced field")
    .test((value, context) => {
      const { createError, schema, path: currentPath } = context;
      const path = useParentErrorPath ? currentPath.replace(/\[\d+]$/, "") : currentPath;

      const message = `${schema.describe().label} references not existing node`;
      const referencedNodeModel = referenceableNodes.find(({ id }) => id === value.referencedNodeId)?.outputModel;
      if (!referencedNodeModel) {
        return createError({ message, path });
      }

      let referencedModel: UnwrappedApiServiceModelDescriptor;

      try {
        referencedModel = getModelByPath(referencedNodeModel, value.referencedField);
      } catch (error) {
        if (error instanceof ModelNotFoundException) {
          return createError({ message, path });
        }
        throw error;
      }

      const evaluateRequiredModelTypeMessage = (defaultMessage: string) => {
        if (requiredModelTypeMessage === undefined) {
          return defaultMessage;
        }
        if (typeof requiredModelTypeMessage === "string") {
          return requiredModelTypeMessage;
        }

        return requiredModelTypeMessage(schema.describe().label);
      };

      if (requiredModelType instanceof Function) {
        const result = requiredModelType(referencedModel, context);
        if (result instanceof yup.ValidationError) {
          return result;
        }
        if (!result) {
          return createError({
            message: evaluateRequiredModelTypeMessage(
              `${schema.describe().label} references field with not matching type`
            ),
            path,
          });
        }
      }

      if (Array.isArray(requiredModelType)) {
        if (!requiredModelType.includes(referencedModel.type)) {
          return createError({
            message: evaluateRequiredModelTypeMessage(
              `${schema.describe().label} must reference a field with type ${requiredModelType.join(" or ")}`
            ),
            path,
          });
        }
      }

      if (value.referencedField.includes(CUSTOM_TOKEN_PLACEHOLDER)) {
        return createError({
          message: "Please add a value to complete the field",
          path,
        });
      }

      return true;
    });
}

export function createReferencedTextAreaSchema({
  referenceableNodes = [],
  isRequired = false,
}: {
  referenceableNodes?: NodeWitOutputModel[];
  isRequired?: boolean;
} = {}) {
  const schema = yup
    .array(
      yup
        .mixed<ReferencedTextAreaValue[0]>()
        .defined()
        .when({
          is: isReferencedNodeValue,
          then: () =>
            createReferencedNodeSchema(referenceableNodes, {
              useParentErrorPath: true,
            }),
          otherwise: () => yup.string().defined(),
        })
    )
    .transform((value, _, ctx) =>
      ctx.isType(value) ? value?.filter((token) => !isReferencedNodeValue(token) || token.referencedNodeId) : []
    );

  if (!isRequired) {
    return schema;
  }

  return schema
    .defined()
    .default([])
    .test(
      "not-empty-array",
      ({ label }) => `${label ?? "This field"} cannot be empty`,
      (value) => value.length > 0 && !value.every((item) => typeof item === "string" && item.trim() === "")
    );
}

export function sanitizePattern(pattern: string): [string, string] {
  let sanitizedPattern = pattern;
  let flags: string = "";
  // javascript regex does not support (?s) flag; use s flag instead
  if (pattern.includes("(?s)")) {
    flags += "s";
    sanitizedPattern = pattern.replace(/\(\?s\)/g, "");
  }
  // if pattern contains \p or \P, it means that it is a unicode regex
  if (/\\[pP]{[^}]+}/.test(pattern)) {
    flags += "u";
  }
  return [sanitizedPattern, flags];
}

export function useApiActionParametersSchema(inputModel: UnwrappedApiServiceModelDescriptor) {
  const { referenceableNodes } = useReferencedFieldContext();
  const [validationSchema, setValidationSchema] = useState(
    generateApiActionParametersSchema(inputModel, true, {
      referenceableNodes,
      model: inputModel,
    })
  );

  useEffect(() => {
    setValidationSchema(
      generateApiActionParametersSchema(inputModel, true, {
        referenceableNodes,
        model: inputModel,
      })
    );
  }, [inputModel, referenceableNodes]);

  return validationSchema;
}

function createRelativeTimeDiffValueSchema() {
  return yup.object({
    builtInVariable: yup.string().oneOf(Object.values(BuiltInVariableType)).required(),
    diff: yup.number().integer().required(),
    unit: yup.string().oneOf(Object.values(TimeFrequency)).required(),
  });
}

export const TimestampFormat = {
  UNKNOWN: undefined,
  UNIX_SECONDS: "X",
  UNIX_MILLISECONDS: "x",
} as const;

export type ApiActionParametersSchemaContext = { castingPhase?: "outgoing" };

export type ApiActionParametersSchema = yup.Schema<unknown, ApiActionParametersSchemaContext, any, "" | "d">;

export function getInitialValueForModel(model: UnwrappedApiServiceModelDescriptor) {
  return generateApiActionParametersSchema(model, true).getDefault();
}

function wrapWithReferencedFieldSchema(
  schemaToWrap: ApiActionParametersSchema,
  context: SchemaContext
): ApiActionParametersSchema {
  if (context.referenceableNodes.length === 0) {
    return schemaToWrap.transform((value) => (isReferencedNodeValue(value) ? schemaToWrap.getDefault() : value));
  }

  const referenceFieldSchema = createReferencedNodeSchema(context.referenceableNodes, {
    requiredModelType: (referencedModel, { path, createError, schema }) => {
      let expectedModel: UnwrappedApiServiceModelDescriptor;

      try {
        expectedModel = getModelByPath(context.model, path.replaceAll(/\[\d+]/g, "").split("."));
      } catch (error) {
        if (error instanceof ModelNotFoundException) {
          return createError({
            message: `${schema.describe().label} references not existing node`,
          });
        }
        throw error;
      }

      if (expectedModel.type === ModelType.LIST) {
        if (
          (referencedModel.type !== ModelType.LIST && expectedModel.member.model.type === referencedModel.type) ||
          (referencedModel.type === ModelType.LIST &&
            expectedModel.member.model.type === referencedModel.member.model.type)
        ) {
          return true;
        }

        const listMemberType = expectedModel.member.model.type;
        return createError({
          message: `${schema.describe().label} must reference a field with type ${listMemberType} or list of ${listMemberType}s`,
        });
      }

      if (
        (referencedModel.type !== ModelType.LIST && referencedModel.type !== expectedModel.type) ||
        (referencedModel.type === ModelType.LIST && referencedModel.member.model.type !== expectedModel.type)
      ) {
        return createError({
          message: `${schema.describe().label} must reference a field with type ${expectedModel.type}`,
        });
      }

      return true;
    },
  }).label(schemaToWrap.describe().label ?? "This field");

  return yup.mixed().when({
    is: isReferencedNodeValue,
    then: () => referenceFieldSchema,
    otherwise: () => schemaToWrap,
  });
}

function getStringSchema({
  model,
  label,
  isRequired = false,
  context,
}: {
  model: StringApiServiceModelDescriptor;
  label: string | undefined;
  isRequired?: boolean;
  context?: SchemaContext;
}) {
  if (model.referenceFieldType === "multiReference") {
    let schema = createReferencedTextAreaSchema({
      referenceableNodes: context?.referenceableNodes,
      isRequired,
    });
    if (label) {
      schema = schema.label(label);
    }
    return schema;
  }

  let schema: yup.StringSchema = yup.string().transform(function t(value) {
    return this.isType(value) ? value : "";
  });
  if (label) {
    schema = schema.label(label);
  }
  if (model.minLength !== undefined) {
    schema = schema.max(model.minLength);
  }
  if (model.maxLength !== undefined) {
    schema = schema.max(model.maxLength);
  }
  if (model.pattern !== undefined) {
    const [sanitizedPattern, flags] = sanitizePattern(model.pattern);
    schema = schema.matches(new RegExp(sanitizedPattern, flags));
  }
  if (model.enum !== undefined) {
    schema = schema.oneOf(model.enum);
  }
  if (isRequired) {
    return schema.required().default("");
  }
  return schema;
}

function getBooleanSchema({ label, isRequired }: { label: string | undefined; isRequired?: boolean }) {
  let schema: yup.BooleanSchema = yup.boolean().transform(function t(value) {
    return this.isType(value) ? value : false;
  });
  if (label) {
    schema = schema.label(label);
  }
  if (isRequired) {
    return schema.required().default(true);
  }
  return schema;
}

function getFloatSchema({
  model,
  label,
  isRequired,
}: {
  model: FloatApiServiceModelDescriptor | IntegerApiServiceModelDescriptor;
  label: string | undefined;
  isRequired?: boolean;
}) {
  let schema: yup.NumberSchema<yup.Maybe<number>> = yup.number();
  if (label) {
    schema = schema.label(label);
  }
  if (model.min !== undefined) {
    schema = schema.min(model.min);
  }
  if (model.max !== undefined) {
    schema = schema.max(model.max);
  }
  if (isRequired) {
    return schema
      .required()
      .transform((val) => (isNaN(val) ? 0 : val))
      .default(0);
  }
  return schema.notRequired().transform((val) => (isNaN(val) ? null : val));
}

function getIntegerSchema({
  model,
  label,
  isRequired,
}: {
  model: IntegerApiServiceModelDescriptor;
  label: string | undefined;
  isRequired?: boolean;
}) {
  return getFloatSchema({ model, label, isRequired }).integer();
}

function getTimestampSchema({
  model,
  label,
  isRequired,
}: {
  model: TimestampApiServiceModelDescriptor;
  label: string | undefined;
  isRequired?: boolean;
}) {
  if (Object.values<string | undefined>(TimestampFormat).includes(model.timestampFormat)) {
    let schema = yup
      .number()
      .integer()
      .transform(function t(value) {
        // yup.number() schema does not support null values, so we need to return -1 as an "empty" value
        return this.isType(value) ? value : -1;
      });

    if (label) {
      schema = schema.label(label);
    }

    if (!isRequired) {
      return schema;
    }

    return schema.default(-1).test(
      "required",
      ({ label }) => `${label ?? "This"} is a required field`,
      (value) => value !== -1
    );
  }

  const schema = yup
    .string()
    .nullable()
    .transform(function t(value) {
      return this.isType(value) ? value : null;
    });

  if (!isRequired) {
    return schema;
  }

  return schema
    .nullable()
    .default(null)
    .test(
      "required",
      ({ label }) => `${label ?? "This"} is a required field`,
      (value) => value !== null
    );
}

function getListSchema({
  model,
  label,
  context,
  isRequired,
}: {
  model: ListApiServiceModelDescriptor<Member>;
  label: string | undefined;
  context?: SchemaContext;
  isRequired?: boolean;
}) {
  let schema = yup
    .array()
    .of(generateApiActionParametersSchema(model.member.model, isRequired, context, model.memberName));
  if (label) {
    schema = schema.label(label);
  }
  if (model.min !== undefined) {
    schema = schema.min(model.min);
  } else if (isRequired) {
    schema = schema.min(1, `${label} field must have at least 1 item`);
  }
  if (model.max !== undefined) {
    schema = schema.max(model.max);
  }

  if (isRequired) {
    return schema
      .required()
      .transform(function t(value) {
        return this.isType(value) ? value : [getInitialValueForModel(model.member.model)];
      })
      .default([getInitialValueForModel(model.member.model)]);
  }

  return schema.transform(function t(value) {
    return this.isType(value) ? value : [];
  });
}

function getStructureSchema({
  model,
  label,
  context,
  isRequired,
}: {
  model: StructureApiServiceModelDescriptor<Member>;
  label: string | undefined;
  context?: SchemaContext;
  isRequired?: boolean;
}) {
  let schema: yup.ObjectSchema<yup.Maybe<yup.AnyObject>> = yup
    .object(
      Object.fromEntries(
        Object.entries(model.members).map(([memberName, member]) => [
          memberName,
          generateApiActionParametersSchema(
            member.model,
            model.requiredMembers?.includes(memberName),
            context,
            memberName
          ),
        ])
      )
    )
    .noUnknown();
  if (label) {
    schema = schema.label(label);
  }
  if (isRequired) {
    return schema.required();
  }
  return schema.optional().default(undefined);
}

function getMapSchema({
  model,
  label,
  context,
  isRequired,
}: {
  model: MapApiServiceModelDescriptor<Member>;
  label: string | undefined;
  context?: SchemaContext;
  isRequired?: boolean;
}) {
  let schema: yup.ArraySchema<any, any, any, any> = yup
    .array()
    .of(
      yup.object({
        [model.keyMemberName]: generateApiActionParametersSchema(
          model.keyMember.model,
          true,
          context,
          model.keyMemberName
        ),
        [model.valueMemberName]: generateApiActionParametersSchema(
          model.valueMember.model,
          true,
          context,
          model.valueMemberName
        ),
      })
    )
    .transform((value) => {
      if (!value) {
        return value;
      }
      if (!Array.isArray(value)) {
        return Object.entries(value).map(([key, value]) => ({
          [model.keyMemberName]: key,
          [model.valueMemberName]: value,
        }));
      }
      return value;
    })
    .when("$castingPhase", ([castingPhase], schema) =>
      castingPhase !== "outgoing"
        ? schema
        : yup
            .object()
            .transform((value) =>
              Object.fromEntries(
                value.map((valueItem) => [valueItem[model.keyMemberName], valueItem[model.valueMemberName]])
              )
            )
    );

  if (model.min !== undefined) {
    schema = schema.min(model.min);
  }
  if (model.max !== undefined) {
    schema = schema.min(model.max);
  }
  if (label) {
    schema = schema.label(label);
  }
  if (isRequired) {
    return schema.required().default([]);
  }

  return schema;
}

export function generateApiActionParametersSchema(
  model: UnwrappedApiServiceModelDescriptor,
  isRequired?: boolean,
  context?: SchemaContext,
  label?: string,
  skipTopLevelReferencedFieldWrapping?: boolean
): ApiActionParametersSchema {
  let schema: ApiActionParametersSchema;

  switch (model.type) {
    case ModelType.STRING: {
      schema = getStringSchema({ model, label, isRequired, context });
      break;
    }
    case ModelType.BOOLEAN: {
      schema = getBooleanSchema({ label, isRequired });
      break;
    }
    case ModelType.INTEGER: {
      schema = getIntegerSchema({ model, label, isRequired });
      break;
    }
    case ModelType.FLOAT: {
      schema = getFloatSchema({ model, label, isRequired });
      break;
    }
    case ModelType.TIMESTAMP: {
      schema = yup.mixed().when({
        is: isRelativeTimeDiffValue,
        then: () => createRelativeTimeDiffValueSchema(),
        otherwise: () => getTimestampSchema({ model, label, isRequired }),
      });
      break;
    }
    case ModelType.LIST: {
      schema = getListSchema({ model, label, context, isRequired });
      break;
    }
    case ModelType.STRUCTURE: {
      schema = getStructureSchema({ model, label, context, isRequired });
      break;
    }
    case ModelType.MAP: {
      schema = getMapSchema({ model, label, context, isRequired });
      break;
    }
    default:
      throw new Error(`Schema generation for model type ${model.type} is not implemented yet.`);
  }

  if (!context || skipTopLevelReferencedFieldWrapping) {
    return schema;
  }

  return wrapWithReferencedFieldSchema(schema, context);
}
